'use client';

import { ensureMetaPixel } from '@/lib/analytics/meta-pixel';
import { reportConversionOnce } from '@/lib/analytics/conversion-once';

/** 注册完成到落地页之间的容忍窗口，超出则视为老用户回访 */
const SIGNUP_WINDOW_MS = 30 * 60 * 1000;

/**
 * 上报 Meta (Facebook) Ads 注册转化：浏览器 Pixel + Conversions API 双通道。
 * 主站与 Studio 共用同一 Pixel 与去重键（event_id = signup_{userId}、localStorage 同源共享），
 * 两处 tracker 不会重复计数。主站布局不带 Meta base code，与购买上报一样按需懒加载。
 * CAPI 走同域 Studio 子应用的接口（Clerk 会话同域共享），token 与实现集中在 Studio。
 */
export async function reportMetaSignupConversion(
  params: { userId: string; createdAtMs: number },
  signal?: AbortSignal,
): Promise<void> {
  const { userId, createdAtMs } = params;
  if (!userId || !createdAtMs) return;

  const elapsed = Date.now() - createdAtMs;
  if (elapsed < 0 || elapsed > SIGNUP_WINDOW_MS) return;

  const eventId = `signup_${userId}`;

  await reportConversionOnce({
    key: `meta_signup_${userId}`,
    persist: 'local',
    // 懒加载仅生产注入；dev 下 fbq 不存在 → 不上报，与其他平台语义一致
    isReady: () => {
      ensureMetaPixel();
      return typeof window.fbq === 'function';
    },
    signal,
    send: () => {
      window.fbq?.('track', 'CompleteRegistration', {}, { eventID: eventId });
      // CAPI 兜底：email/userId 由服务端从 Clerk 会话取，这里只传事件标识
      void fetch('/studio/api/analytics/meta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event: 'CompleteRegistration', eventId }),
        keepalive: true,
      }).catch(() => {
        // 旁路上报，失败静默
      });
    },
  });
}
